import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { TextFormatButtons, TokenInsertButtons, appendToken } from './tokenTools';
import IconClassPicker from './IconClassPicker';

const subItemMove = (items, id, dir) => {
  const idx = items.findIndex((i) => i.id === id);
  const ni = idx + dir;
  if (ni < 0 || ni >= items.length) return items;
  const next = [...items];
  [next[idx], next[ni]] = [next[ni], next[idx]];
  return next;
};

export default function ListEditor({ type, content, update }) {
  const [activeTarget, setActiveTarget] = useState(null);

  const insertToken = (token) => {
    if (!activeTarget) return;

    if (activeTarget.startsWith('bullet:')) {
      const itemId = activeTarget.split(':')[1];
      const items = content.items || [];
      update('items', items.map((item) => (item.id === itemId ? { ...item, text: appendToken(item.text || '', token) } : item)));
      return;
    }

    if (activeTarget.startsWith('icon:')) {
      const [, itemId, field] = activeTarget.split(':');
      const items = content.items || [];
      update('items', items.map((item) => (item.id === itemId ? { ...item, [field]: appendToken(item[field] || '', token) } : item)));
      return;
    }

    if (activeTarget.startsWith('faq:')) {
      const [, itemId, field] = activeTarget.split(':');
      const items = content.items || [];
      update('items', items.map((item) => (item.id === itemId ? { ...item, [field]: appendToken(item[field] || '', token) } : item)));
    }
  };

  switch (type) {

    case 'bulletList': {
      const items = content.items || [];
      const addItem = () => update('items', [...items, { id: `li-${Date.now()}`, text: '' }]);
      const removeItem = (iid) => update('items', items.filter((i) => i.id !== iid));
      const updateItem = (iid, val) => update('items', items.map((i) => i.id === iid ? { ...i, text: val } : i));
      const moveItem = (iid, dir) => update('items', subItemMove(items, iid, dir));
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>List style</Form.Label>
            <Form.Select value={content.style || 'bullet'} onChange={(e) => update('style', e.target.value)}>
              <option value="bullet">• Bullet list</option>
              <option value="numbered">1. Numbered list</option>
            </Form.Select>
          </Form.Group>
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">Items ({items.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addItem}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Item
            </button>
          </div>
          {items.length === 0 && <p className="pagebuilder-sublist-empty">No items yet.</p>}
          {items.map((item, idx) => (
            <div key={item.id} className="pagebuilder-sublist-item pagebuilder-sublist-item--row">
              <div className="pagebuilder-block-actions">
                <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveItem(item.id, -1)} disabled={idx === 0} title="Move up"><i className="bi bi-arrow-up" /></button>
                <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveItem(item.id, 1)} disabled={idx === items.length - 1} title="Move down"><i className="bi bi-arrow-down" /></button>
              </div>
              <Form.Control
                style={{ flex: 1 }}
                value={item.text}
                onChange={(e) => updateItem(item.id, e.target.value)}
                onFocus={() => setActiveTarget(`bullet:${item.id}`)}
                placeholder={`Item ${idx + 1}…`}
              />
              <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeItem(item.id)} title="Remove">
                <i className="bi bi-trash" />
              </button>
            </div>
          ))}
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );
    }

    case 'iconList': {
      const items = content.items || [];
      const addItem = () => update('items', [...items, { id: `ili-${Date.now()}`, icon: 'bi bi-check-circle', text: '' }]);
      const removeItem = (iid) => update('items', items.filter((i) => i.id !== iid));
      const updateItem = (iid, field, val) => update('items', items.map((i) => i.id === iid ? { ...i, [field]: val } : i));
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">Items ({items.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addItem}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Item
            </button>
          </div>
          {items.length === 0 && <p className="pagebuilder-sublist-empty">No items yet.</p>}
          {items.map((item, idx) => (
            <div key={item.id} className="pagebuilder-sublist-item">
              <div className="pagebuilder-sublist-item-header">
                <span className="pagebuilder-sublist-index">#{idx + 1}</span>
                <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeItem(item.id)} title="Remove"><i className="bi bi-trash" /></button>
              </div>
              <div className="pagebuilder-field-row">
                <Form.Group style={{ flex: '0 0 180px' }}>
                  <Form.Label>Icon class</Form.Label>
                  <Form.Control
                    value={item.icon}
                    onChange={(e) => updateItem(item.id, 'icon', e.target.value)}
                    onFocus={() => setActiveTarget(`icon:${item.id}:icon`)}
                    placeholder="bi bi-check-circle"
                  />
                </Form.Group>
                <Form.Group style={{ flex: 1 }}>
                  <Form.Label>Text</Form.Label>
                  <Form.Control
                    value={item.text}
                    onChange={(e) => updateItem(item.id, 'text', e.target.value)}
                    onFocus={() => setActiveTarget(`icon:${item.id}:text`)}
                    placeholder="Item text…"
                  />
                </Form.Group>
                {item.icon && <i className={`${item.icon} pagebuilder-icon-preview`} aria-hidden="true" />}
              </div>
            </div>
          ))}
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );
    }

    case 'faq': {
      const items = content.items || [];
      const addItem = () => update('items', [...items, { id: `faq-${Date.now()}`, question: '', answer: '' }]);
      const removeItem = (iid) => update('items', items.filter((i) => i.id !== iid));
      const updateItem = (iid, field, val) => update('items', items.map((i) => i.id === iid ? { ...i, [field]: val } : i));
      const moveItem = (iid, dir) => update('items', subItemMove(items, iid, dir));
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">FAQ Items ({items.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addItem}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Question
            </button>
          </div>
          {items.length === 0 && <p className="pagebuilder-sublist-empty">No questions yet.</p>}
          {items.map((item, idx) => (
            <div key={item.id} className="pagebuilder-sublist-item">
              <div className="pagebuilder-sublist-item-header">
                <span className="pagebuilder-sublist-index">{idx + 1}</span>
                <div className="pagebuilder-block-actions">
                  <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveItem(item.id, -1)} disabled={idx === 0} title="Move up"><i className="bi bi-arrow-up" /></button>
                  <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveItem(item.id, 1)} disabled={idx === items.length - 1} title="Move down"><i className="bi bi-arrow-down" /></button>
                  <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeItem(item.id)} title="Remove"><i className="bi bi-trash" /></button>
                </div>
              </div>
              <Form.Group className="mb-2">
                <Form.Label>Question</Form.Label>
                <Form.Control
                  value={item.question || ''}
                  onChange={(e) => updateItem(item.id, 'question', e.target.value)}
                  onFocus={() => setActiveTarget(`faq:${item.id}:question`)}
                  placeholder="Question…"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Answer</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={item.answer || ''}
                  onChange={(e) => updateItem(item.id, 'answer', e.target.value)}
                  onFocus={() => setActiveTarget(`faq:${item.id}:answer`)}
                  placeholder="Answer…"
                />
              </Form.Group>
              <IconClassPicker
                value={item.icon || ''}
                label="FAQ Icon (optional)"
                placeholder="Search FAQ icon..."
                maxRows={2}
                onChange={(nextIconClass) => updateItem(item.id, 'icon', nextIconClass)}
              />
            </div>
          ))}
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );
    }

    case 'toggleBlock':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>Toggle Label</Form.Label>
            <Form.Control value={content.label || ''} onChange={(e) => update('label', e.target.value)} placeholder="Click to expand…" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Hidden Content</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={content.content || ''}
              onChange={(e) => update('content', e.target.value)}
              placeholder="Content shown when toggled open…"
            />
          </Form.Group>
        </div>
      );

    default:
      return null;
  }
}
